/**
 * @file        src/pages/Dashboard.jsx
 * @description Admin dashboard. KPIs + revenue chart + today's status + recent ledger.
 *
 *              AUDIENCE IS THE SCREEN, NOT A FILTER. This component backs two
 *              sidebar entries — Dashboard (B2C) and Corporate Dashboard (B2B,
 *              see CorporateDashboard.jsx) — and every figure on it is scoped to
 *              that one audience. The single exception is Cash Balance, which is
 *              the live SUPPLIER cash position (TripJack), not a customer
 *              segment; it is whole-business on both screens and labelled so.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-25 Claude — REMOVED the Supplier Account panel. Deposit / Credit /
 *     Dues are already one click away under the topbar's Cash Balance pill
 *     (Topbar.jsx → SupplierBalances), which is the same component reading the
 *     same query, so the panel restated the pill's own
 *     three figures a few hundred pixels below it. Client decision 2026-09-25:
 *     one place per figure. The pill keeps them; nothing was lost from the
 *     console.
 *   - 2026-09-24 Claude — The two dashboards are now reachable from each other by
 *     a B2C / Corporate TAB STRIP, and every open queue in the console is shown
 *     on a "Needs action" panel beside Today's Status.
 *     THE TABS ARE ROUTES, NOT A PICKER, which is the whole distinction §9d
 *     turns on: each tab is its own URL with its own fixed `audience` prop, so
 *     the audience still cannot be set from the query string, a shared link
 *     still names one audience, and each screen still asks the API for one
 *     audience only. The "Customer type" segment removed on 2026-09-15 is NOT
 *     coming back — there is no "All" tab, and there is nothing here holding an
 *     audience in component state.
 *     The pending-actions panel splits its own counts into this audience's
 *     queues and a separately headed whole-business block, for the same reason
 *     Cash Balance is labelled "(all business)": three of the eleven queues have
 *     no customer type, and counting them under both headings would double them.
 *   - 2026-09-15 Claude — SPLIT into Dashboard (B2C) + Corporate Dashboard (B2B).
 *     The "Customer type" segment (All / B2C / B2B) is gone. It had only ever
 *     filtered the revenue CHART: Lifetime Volume, Pending Refunds and the whole
 *     Recent Transactions ledger were global, so picking B2B redrew one panel
 *     and left every other number showing the whole business — the chart
 *     disagreed with the tiles directly above it. All three endpoints now take
 *     the screen's audience (see routes/dashboard.routes.js), and Today's Status
 *     shows only this audience's two rows instead of all four.
 *   - 2026-09-15 Claude — Three tiles stopped lying:
 *     • CREDIT LIMIT was the hardcoded string "₹0" with trend "0%" — no backend
 *       field existed. It now shows the sanctioned corporate credit with the
 *       drawn amount as its sub-label, and is rendered on the CORPORATE
 *       dashboard only: a credit line exists only on a corporate account, so on
 *       the B2C screen it could only ever be ₹0, which is what made the
 *       placeholder look plausible for so long. The B2C screen shows the
 *       refund-queue VALUE in that slot instead.
 *     • LIFETIME VOLUME's "+8.1%" was likewise a literal with nothing behind it.
 *       Removed rather than invented — there is no prior-period figure here.
 *     • REFUNDS QUEUE inside "Today's Status" rendered the LIFETIME pending
 *       count while the two rows above it were CURDATE()-scoped. It now reads
 *       `today.refunds_queue`, and the lifetime figure stays on its own KPI tile
 *       where it belongs.
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-26 Claude — Recent Transactions "Filter" button now works: it opens
 *     a product + status filter that is applied server-side.
 *   - 2026-09-24 Claude — Supplier Account subtitle names a derived credit and
 *                          a no-credit-facility account, so ₹0.00 credit/dues
 *                          is explained rather than left to look like a gap.
 *   - 2026-09-21 Claude — Added the Supplier Account panel (Deposit / Credit /
 *                          Dues) under the KPI row. The Cash Balance tile is the
 *                          DEPOSIT figure only, so an account running on its
 *                          credit line showed ₹0.00 with no sign of the ₹5.9L it
 *                          could actually spend or the ₹1.05L it owed.
 *   - 2026-07-30 Claude — Cash Balance is now the LIVE supplier (TripJack) wallet
 *     balance for a tenant_admin, and "NA" for a super_admin (no API call). It
 *     previously showed a locally-derived figure (lifetime − refunded) for
 *     everyone, which was never the supplier's actual cash position.
 *   - 2026-08-03 Claude — Payments quick-link now targets /payments/banks; its
 *     old target /payments/settings was removed from nav and 404-bounced home.
 *   - 2026-08-06 Claude — Payments quick-link retargeted to /payments/tax:
 *     Bank List (/payments/banks) was removed as an unused admin feature.
 *   - 2026-08-03 Claude — Brand segment is registry-driven (useBrands) instead of
 *     the hardcoded Neon/Beyond pair; brands are per-tenant data now.
 *   - 2026-08-04 Claude — Recent Transactions reads the Transaction Log's
 *     booking-grouped ledger: rows are keyed by booking REFERENCE, wallet money
 *     is included, and Debit/Credit come from the ledger's own columns (Neon's
 *     books) instead of being guessed from status. 'Cancelled' joins the status
 *     filter — the ledger no longer disguises it as Refunded.
 *   - 2026-08-04 Claude — Today's Status shows B2B Successful/Failed alongside
 *     B2C (summary now splits by the booking user's role; B2B was missing).
 *   - 2026-08-04 Claude — Recent Transactions now shows the applied filters as
 *     removable chips, visible even with the filter panel collapsed — the
 *     "Filter · N" count on the toggle button said how many but never which.
 *   - 2026-08-05 Claude — One-time-per-session "email delivery isn't fully
 *     configured" banner, driven by the same /api/email-settings/status the
 *     topbar pill reads. Dismissible (sessionStorage), reappears next login.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHead from '../components/PageHead';
import Icon from '../components/Icon';
import Chip from '../components/Chip';
import Select from '../components/Select';
import TableSkeleton from '../components/TableSkeleton';
import { get } from '../api/client';
import { useBrands } from '../api/brands';
import { qk } from '../api/queryKeys';
import { getEmailSettingsStatus, unwrapList } from '../api/emailSettings';
import { inr, inrCompact, formatDate } from '../lib/format';
import { useAuth } from '../lib/auth.store';
import { useWalletBalance } from '../lib/useWalletBalance';
import AudienceTabs from '../components/AudienceTabs';
import PendingActions from '../components/PendingActions';

const EMAIL_BANNER_DISMISS_KEY = 'nh_email_banner_dismissed';

const RANGES = ['12m', '30d', '7d'];
const RANGE_LABEL = { '12m': 'last 12 months', '30d': 'last 30 days', '7d': 'last 7 days' };
// Chart-header segmented filters — same .seg control as the range toggle beside
// them. The brand segment is built per-render from the tenant's brand registry.
// Recent-ledger filter options. Values are what /dashboard/recent-transactions
// whitelists — statuses are the mapped ledger values, not raw booking_status.
const LEDGER_FLOW_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'insurance', label: 'Insurance' },
];
const LEDGER_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Success', label: 'Success' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Refunded', label: 'Refunded' },
  { value: 'Initiated', label: 'Initiated' },
  { value: 'Awaiting confirmation', label: 'Awaiting confirmation' },
  { value: 'Cancellation Requested', label: 'Cancellation Requested' },
];

export default function Dashboard({
  title,
  // Which audience this SCREEN is. Fixed — see the note in the file header.
  audience = 'b2c',
} = {}) {
  const user = useAuth((s) => s.user);
  // Live supplier cash position (tenant_admin) / "NA" (super_admin, no call).
  const wallet = useWalletBalance();
  const [range, setRange] = useState('12m');
  const [dashBrand, setDashBrand] = useState('');     // '' (All) | brand code
  const isB2B = audience === 'b2b';
  // Registry-driven brand segment: All + whatever brands this tenant runs.
  const { options: brandOptions } = useBrands();
  const brandSeg = [{ value: '', label: 'All' }, ...brandOptions];

  const { data: summary, isLoading: sLoading } = useQuery({
    queryKey: [...qk.dashboard.summary, audience],
    queryFn: () => get('/dashboard/summary', { params: { audience } }).then((r) => r.data),
    staleTime: 60_000,
  });
  // Recent-ledger filters. The panel only holds a handful of rows, so these are
  // sent to the server — filtering 6 client-side rows would just blank the table.
  const [showLedgerFilter, setShowLedgerFilter] = useState(false);
  const [ledgerFlow, setLedgerFlow] = useState('');     // '' | flight | hotel | insurance
  const [ledgerStatus, setLedgerStatus] = useState(''); // '' | Success | Pending | ...
  const ledgerParams = {
    audience,
    ...(ledgerFlow ? { flowType: ledgerFlow } : {}),
    ...(ledgerStatus ? { status: ledgerStatus } : {}),
  };
  const activeLedgerFilters = Object.keys(ledgerParams).length;

  const { data: recent, isLoading: rLoading } = useQuery({
    queryKey: [...qk.dashboard.recent, audience, ledgerFlow, ledgerStatus],
    queryFn: () => get('/dashboard/recent-transactions', { params: ledgerParams }).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
  // Keyed on the range, so switching 12M/30D/7D actually refetches — the three
  // buttons used to be decoration with no handler and a hardcoded active class.
  const { data: series } = useQuery({
    queryKey: [...qk.dashboard.series, range, dashBrand, audience],
    queryFn: () => {
      const p = new URLSearchParams({ range, audience });
      if (dashBrand) p.set('brand', dashBrand);
      return get(`/dashboard/revenue-series?${p.toString()}`).then((r) => r.data);
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });

  const firstName = (user?.first_name || 'Neon').trim();

  // Email delivery health banner — same status feed as the topbar pill.
  // Unconfigured (empty rows) reads as "not fully configured" too, same as
  // the pill's worst-status fallback; a fresh tenant with zero brands set up
  // needs this nudge just as much as one with a broken row.
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(
    () => sessionStorage.getItem(EMAIL_BANNER_DISMISS_KEY) === '1',
  );
  const { data: emailStatusRaw } = useQuery({
    queryKey: qk.emailSettings.status,
    queryFn: getEmailSettingsStatus,
    enabled: !wallet.isSuperAdmin,
    staleTime: 60_000,
  });
  const emailRows = unwrapList(emailStatusRaw);
  const emailNeedsAttention = emailRows.length === 0 || emailRows.some((r) => r.status !== 'connected');

  return (
    <>
      <PageHead
        title={title || <>Welcome back, <span className="grad-text">{firstName}</span></>}
        subtitle={`${isB2B ? 'Corporate (B2B) operations' : 'Retail (B2C) operations'} · ${formatDate(new Date())}`}
        /* Export hidden for now — dashboard export not built yet. */
      />

      {/* Two routes, one strip. See AudienceTabs.jsx for why these are links. */}
      <AudienceTabs
        tabs={[
          { to: '/', label: 'B2C / Retail', end: true },
          { to: '/corporate-dashboard', label: 'Corporate' },
        ]}
      />

      {!wallet.isSuperAdmin && emailNeedsAttention && !emailBannerDismissed && (
        <div className="notice-banner">
          <span>
            Email delivery isn't fully configured — some notifications may not be sent.{' '}
            <Link to="/account/email-settings">Fix now →</Link>
          </span>
          <button
            type="button"
            className="dismiss"
            aria-label="Dismiss"
            onClick={() => { sessionStorage.setItem(EMAIL_BANNER_DISMISS_KEY, '1'); setEmailBannerDismissed(true); }}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="kpi-grid">
        {/* The trend label is "Live" rather than a percentage: this is a
            supplier balance read fresh each load, with no prior figure to
            compare it against. */}
        {/* Supplier-side cash, not a customer segment — the SAME figure on both
            dashboards. Labelled "(all business)" so it is not misread as this
            screen's B2C/B2B number. */}
        <Kpi tone="teal" icon="wallet" label="Cash Balance (all business)"
             value={wallet.display} loading={wallet.isLoading}
             trend="flat" trendLabel={wallet.isSuperAdmin ? '—' : 'Live'} />
        {/* Corporate-only: a credit line is a property of a business_accounts
            row, so on the retail dashboard this tile could only ever read ₹0.
            The B2C screen gets the refund queue's VALUE in the slot instead,
            which is a number that screen actually owns. */}
        {isB2B ? (
          <Kpi tone="violet" icon="card" label="Credit Limit"
               value={summary ? inrCompact(summary.credit_limit) : '—'} loading={sLoading}
               trend="flat"
               trendLabel={summary ? `${inrCompact(summary.credit_used)} drawn` : '—'} />
        ) : (
          <Kpi tone="violet" icon="card" label="Refunds Value"
               value={summary ? inrCompact(summary.pending_refunds_amount) : '—'} loading={sLoading}
               trend="flat" trendLabel="awaiting payout" />
        )}
        {/* Flight · Hotel · Insurance — the same three products the chart below
            plots. The tile counted only the first two until 2026-09-15, so it
            sat under a chart it disagreed with by exactly the insurance take. */}
        <Kpi tone="amber" icon="trend" label="Lifetime Volume"
             value={summary ? inrCompact(summary.lifetime_volume) : '—'} loading={sLoading}
             trend="flat" trendLabel="all products" />
        <Kpi tone="rose" icon="clock" label="Pending Refunds"
             value={summary?.pending_refunds ?? '—'} loading={sLoading}
             trend="down"
             trendLabel={summary ? `${inrCompact(summary.pending_refunds_amount)} open` : '—'} />
      </div>

      {/* Main grid */}
      <div className="dash-grid">
        <article className="panel glass">
          <div className="panel-head">
            <div>
              <h2>Booking Revenue</h2>
              <p className="muted">Flight · Hotel · Insurance — {RANGE_LABEL[range]}</p>
            </div>
            <div className="panel-head-controls" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="seg" role="group" aria-label="Brand">
                {brandSeg.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    className={dashBrand === b.value ? 'active' : undefined}
                    onClick={() => setDashBrand(b.value)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <div className="seg">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={range === r ? 'active' : undefined}
                    onClick={() => setRange(r)}
                  >
                    {r.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* The legend lives inside the chart now — it carries each product's
              total, so it needs the same data the lines are drawn from. */}
          <RevenueChart data={series} range={range} />
        </article>

        <article className="panel glass">
          <div className="panel-head"><h2>Today's Status</h2></div>
          <div className="stat-list">
            {/* This screen's audience only — the other half lives on the other
                dashboard. `today` carries both, so nothing extra is fetched. */}
            <StatRow tone="teal" icon="check" label="Successful"
                     hint={isB2B ? 'Corporate flight & hotel' : 'Flight & hotel confirmed'}
                     value={summary?.today?.[isB2B ? 'b2b_success' : 'b2c_success'] ?? '—'} />
            <StatRow tone="rose" icon="x" label="Failed"
                     hint={isB2B ? 'Gateway / wallet / supplier' : 'Gateway / supplier'}
                     value={summary?.today?.[isB2B ? 'b2b_failed' : 'b2c_failed'] ?? '—'} />
            {/* TODAY's additions to the queue — this panel is CURDATE()-scoped.
                It used to render the lifetime figure, so the panel headed
                "Today's Status" showed a number covering all time. The lifetime
                count still has its own KPI tile above. */}
            <StatRow tone="violet" icon="refund" label="Refunds Queue" hint="Raised today"
                     value={summary?.today?.refunds_queue ?? '—'} />
          </div>
          <div className="quick-links">
            {/* Point at THIS audience's lists — a Corporate dashboard linking to
                the B2C Booking List is the same mixing the split removes. */}
            <Link to={isB2B ? '/account/corporate-accounts' : '/customers'}>
              <Icon name="users" /> {isB2B ? 'Corporate Accounts' : 'Customers'}
            </Link>
            <Link to={isB2B ? '/flight/corporate-bookings' : '/flight/bookings'}>
              <Icon name="flight" /> Flight List
            </Link>
            <Link to={isB2B ? '/hotel/corporate-bookings' : '/hotel/bookings'}>
              <Icon name="bed" /> Hotel List
            </Link>
            <Link to="/payments/tax"><Icon name="card" /> Payments</Link>
          </div>
        </article>
      </div>

      {/* Every open queue this viewer may see. Below the chart rather than above
          it: the KPIs answer "how is the business doing", this answers "what is
          waiting", and the second question is the one you act on. */}
      <PendingActions audience={audience} />

      {/* Recent ledger */}
      <article className="panel glass">
        <div className="panel-head">
          <div><h2>Recent Transactions</h2><p className="muted">Latest ledger activity</p></div>
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => setShowLedgerFilter((v) => !v)}
            aria-expanded={showLedgerFilter}
          >
            <Icon name="filter" /> Filter{activeLedgerFilters ? ` · ${activeLedgerFilters}` : ''}
          </button>
        </div>
        {/* Applied filters stay visible even when the panel is collapsed — the
            "Filter · N" count on the toggle above only says how many, not which. */}
        {activeLedgerFilters > 0 && (
          <div className="active-filters" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 10px' }}>
            {ledgerFlow && (
              <Chip tone="pend">
                {LEDGER_FLOW_OPTIONS.find((o) => o.value === ledgerFlow)?.label || ledgerFlow}
                <button
                  type="button"
                  className="chip-remove"
                  aria-label="Remove product filter"
                  onClick={() => setLedgerFlow('')}
                  style={{ marginLeft: 6, background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0 }}
                >
                  ×
                </button>
              </Chip>
            )}
            {ledgerStatus && (
              <Chip tone="pend">
                {ledgerStatus}
                <button
                  type="button"
                  className="chip-remove"
                  aria-label="Remove status filter"
                  onClick={() => setLedgerStatus('')}
                  style={{ marginLeft: 6, background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0 }}
                >
                  ×
                </button>
              </Chip>
            )}
          </div>
        )}
        {showLedgerFilter && (
          <div className="search-row">
            <div className="field">
              <label>Product</label>
              <Select
                value={ledgerFlow}
                onChange={setLedgerFlow}
                options={LEDGER_FLOW_OPTIONS}
              />
            </div>
            <div className="field">
              <label>Status</label>
              <Select
                value={ledgerStatus}
                onChange={setLedgerStatus}
                options={LEDGER_STATUS_OPTIONS}
              />
            </div>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => { setLedgerFlow(''); setLedgerStatus(''); }}
              disabled={!activeLedgerFilters}
            >
              Clear
            </button>
          </div>
        )}
        <div className="table-wrap">
          {rLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead><tr>
                <th>Booking Ref</th><th>Detail</th>
                <th title="Money paid out by Neon — supplier fare + refunds">Debit</th>
                <th title="Money received by Neon — wallet and gateway combined">Credit</th>
                <th>Status</th><th>Date</th>
              </tr></thead>
              <tbody>
                {(recent || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      {activeLedgerFilters ? 'No transactions match these filters.' : 'No transactions yet.'}
                    </td>
                  </tr>
                ) : (recent || []).map((t) => (
                  <tr key={t.row_key}>
                    <td className="mono">{t.booking_reference || `ID-${t.booking_id}`}</td>
                    <td>{labelFor(t)}</td>
                    <td className="deb">{Number(t.debit_amount) > 0 ? inr(t.debit_amount) : '—'}</td>
                    <td className="cre">{Number(t.credit_amount) > 0 ? inr(t.credit_amount) : '—'}</td>
                    <td><Chip tone={chipTone(t.status)}>{t.status}</Chip></td>
                    <td className="muted">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The products we chart. Insurance sells real policies now, so it gets its own
// line rather than being quietly missing from "revenue".
export const SERIES = [
  { key: 'flight',    label: 'Flight',    colour: '#2dd4bf' },
  { key: 'hotel',     label: 'Hotel',     colour: '#a78bfa' },
  { key: 'insurance', label: 'Insurance', colour: '#f59e0b' },
];

/** "2026-07" → "Jul"; "2026-07-14" → "14 Jul". */
function bucketLabel(key) {
  const [y, m, d] = key.split('-');
  const mon = MONTH_LABELS[Number(m) - 1] ?? '';
  return d ? `${Number(d)} ${mon}` : mon;
}

/** A "nice" axis ceiling — 1/2/5 × 10ⁿ — so gridlines land on readable numbers. */
function niceMax(v) {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function RevenueChart({ data, range }) {
  // The server sends every bucket in the window, including the ones that sold
  // nothing — so a quiet week renders as a flat line, not a gap.
  const buckets = data?.buckets ?? [];
  const rows = data?.rows ?? [];
  const [hover, setHover] = useState(null); // bucket index under the cursor

  const byKey = {};
  for (const r of rows) {
    byKey[r.bucket] = byKey[r.bucket] || {};
    byKey[r.bucket][r.flow_type] = Number(r.total) || 0;
  }
  const lines = SERIES.map((s) => ({
    ...s,
    values: buckets.map((b) => byKey[b]?.[s.key] || 0),
  }));

  // What the admin actually wants to know: how much, per product, over this window.
  const totals = lines.map((l) => ({ ...l, total: l.values.reduce((a, b) => a + b, 0) }));
  const grand = totals.reduce((a, t) => a + t.total, 0);

  const W = 600, TOP = 20, BASE = 210;
  const peak = Math.max(...lines.flatMap((l) => l.values), 0);
  // Scale to a rounded ceiling, not the raw peak: a gridline reading "₹12,480"
  // tells you nothing you can compare against.
  const max = niceMax(peak) || 1;
  const x = (i) => (buckets.length <= 1 ? W / 2 : (i / (buckets.length - 1)) * W);
  const y = (v) => BASE - (v / max) * (BASE - TOP);

  const linePath = (arr) => arr.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const areaPath = (arr) => `${linePath(arr)} L${x(arr.length - 1).toFixed(1)} ${BASE} L0 ${BASE} Z`;

  const hasData = peak > 0;
  const window = range === '7d' ? 'last 7 days' : range === '30d' ? 'last 30 days' : 'last 12 months';

  // Four horizontal gridlines, labelled with the value they represent.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ frac: f, value: max * f }));

  // ~6 evenly-spaced labels, whatever the bucket count.
  const step = Math.max(1, Math.ceil(buckets.length / 6));
  const axisIdx = buckets.map((_, i) => i).filter((i) => i % step === 0 || i === buckets.length - 1);

  // Nearest bucket to the cursor. The SVG is stretched (preserveAspectRatio=none),
  // so hit-testing must use the DOM box, not SVG coordinates.
  const onMove = (e) => {
    if (!buckets.length) return;
    const box = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    setHover(Math.round(frac * (buckets.length - 1)));
  };

  const plotTop = (TOP / 220) * 100;              // % — the SVG's usable band,
  const plotHeight = ((BASE - TOP) / 220) * 100;  //     so HTML overlays line up

  return (
    <div>
      {/* Headline: the single number the chart is about. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <strong style={{ fontSize: 22 }}>{inr(grand)}</strong>
        <span className="muted" style={{ fontSize: 12 }}>
          total confirmed revenue · {window}
        </span>
      </div>

      <div className="chart" style={{ position: 'relative', paddingLeft: 52 }}>
        {/* Y axis. Without it the chart shows a shape and no magnitude — a ₹500
            week and a ₹5,00,000 week draw an identical line. */}
        {ticks.map((t) => (
          <div
            key={t.frac}
            style={{
              position: 'absolute', left: 0, right: 0,
              top: `${plotTop + plotHeight * (1 - t.frac)}%`,
              borderTop: '1px dashed rgba(148,163,184,.25)',
              pointerEvents: 'none',
            }}
          >
            <span
              className="muted"
              style={{ position: 'absolute', left: 0, transform: 'translateY(-50%)', fontSize: 10 }}
            >
              {t.value >= 1000 ? inrCompact(t.value) : inr(t.value)}
            </span>
          </div>
        ))}

        <div
          style={{ position: 'relative' }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <svg viewBox="0 0 600 220" preserveAspectRatio="none" className="area">
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={s.colour} stopOpacity=".45" />
                  <stop offset="1" stopColor={s.colour} stopOpacity="0" />
                </linearGradient>
              ))}
            </defs>
            {lines.map((l) => (
              <g key={l.key}>
                <path d={areaPath(l.values)} fill={`url(#fill-${l.key})`} />
                <path d={linePath(l.values)} fill="none" stroke={l.colour} strokeWidth="2.5" />
              </g>
            ))}
            {hover != null && (
              <line
                x1={x(hover)} x2={x(hover)} y1={TOP} y2={BASE}
                stroke="rgba(148,163,184,.6)" strokeWidth="1" strokeDasharray="3 3"
              />
            )}
          </svg>

          {/* Readout for the hovered day/month — the actual figures, per product. */}
          {hover != null && hasData && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${(hover / Math.max(1, buckets.length - 1)) * 100}%`,
                // Flip the card to the left of the guide near the right edge, so it
                // never spills outside the panel.
                transform: hover > buckets.length / 2 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
                background: 'rgba(15,23,42,.96)',
                color: '#e2e8f0',
                border: '1px solid rgba(148,163,184,.25)',
                borderRadius: 10,
                padding: '8px 10px',
                fontSize: 11,
                lineHeight: 1.6,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 2 }}>{bucketLabel(buckets[hover])}</div>
              {lines.map((l) => (
                <div key={l.key} style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                  <span>
                    <i style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                      background: l.colour, marginRight: 6,
                    }} />
                    {l.label}
                  </span>
                  <strong>{inr(l.values[hover] || 0)}</strong>
                </div>
              ))}
              <div style={{ borderTop: '1px solid rgba(148,163,184,.25)', marginTop: 4, paddingTop: 3, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <span>Total</span>
                <strong>{inr(lines.reduce((a, l) => a + (l.values[hover] || 0), 0))}</strong>
              </div>
            </div>
          )}
        </div>

        {!hasData ? <div className="chart-empty muted">No revenue in the {window} yet.</div> : null}
        <div className="x-axis">
          {axisIdx.map((i) => <span key={i}>{bucketLabel(buckets[i])}</span>)}
        </div>
      </div>

      {/* Legend doubles as the per-product total — the split is the point of the
          three lines, so give the number, not just the colour. */}
      <div className="legend">
        {totals.map((t) => (
          <span key={t.key}>
            <i className="lg" style={{ background: t.colour }} /> {t.label}
            <strong style={{ marginLeft: 6 }}>{inr(t.total)}</strong>
            <span className="muted" style={{ marginLeft: 4 }}>
              ({grand ? Math.round((t.total / grand) * 100) : 0}%)
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Kpi({ tone, icon, label, value, trend, trendLabel, loading }) {
  return (
    <article className="kpi glass neo">
      <div className="kpi-top">
        <span className={`kpi-ic ${tone}`}><Icon name={icon} /></span>
        <span className={`trend ${trend || 'flat'}`}>{trendLabel}</span>
      </div>
      <h3>{loading ? <span className="skeleton" style={{ display: 'inline-block', width: 80, height: 24 }} /> : value}</h3>
      <p>{label}</p>
    </article>
  );
}

function StatRow({ tone, icon, label, hint, value }) {
  return (
    <div className="stat-row">
      <span className={`s-ic ${tone}`}><Icon name={icon} /></span>
      <div><b>{label}</b><small>{hint}</small></div>
      <b className="s-val">{value}</b>
    </div>
  );
}

const FLOW_LABEL = { hotel: 'Hotel Booking', insurance: 'Insurance Policy', flight: 'Flight Booking' };

function labelFor(p) {
  return `${FLOW_LABEL[p.flow_type] || 'Flight Booking'} (ID-${p.booking_id ?? '—'})`;
}

function chipTone(status) {
  if (status === 'Success') return 'ok';
  if (status === 'Refunded' || status === 'Cancelled') return 'warn';
  if (status === 'Failed') return 'rej';
  return 'pend';
}
